import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// =====================================================
// ORGANIZATIONS
// =====================================================

export const organizationHelpers = {
  // Get all organizations for the current user
  async getUserOrganizations(userId) {
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        role,
        joined_at,
        organization:organizations(*)
      `)
      .eq('user_id', userId);

    if (error) throw error;
    return data.map((item) => ({
      ...item.organization,
      userRole: item.role,
      joinedAt: item.joined_at,
    }));
  },

  // Create new organization
  async createOrganization(name, slug, userId) {
    // Create organization
    const { data: org, error: orgError } = await supabase
      .from('organizations')
      .insert({ name, slug, is_active: true })
      .select()
      .single();

    if (orgError) throw orgError;

    // Add creator as owner
    const { error: memberError } = await supabase
      .from('organization_members')
      .insert({
        organization_id: org.id,
        user_id: userId,
        role: 'owner',
      });

    if (memberError) throw memberError;

    return org;
  },

  // Update organization
  async updateOrganization(orgId, updates) {
    const { data, error } = await supabase
      .from('organizations')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', orgId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get organization by ID
  async getOrganization(orgId) {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .single();

    if (error) throw error;
    return data;
  },
};

// =====================================================
// SERVICES
// =====================================================

export const serviceHelpers = {
  // Get all services for an organization
  async getServices(organizationId) {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get service by ID
  async getService(serviceId) {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('id', serviceId)
      .single();

    if (error) throw error;
    return data;
  },

  // Create service
  async createService(service) {
    const { data, error } = await supabase
      .from('services')
      .insert(service)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update service
  async updateService(serviceId, updates) {
    const { data, error } = await supabase
      .from('services')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', serviceId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete service
  async deleteService(serviceId) {
    const { error } = await supabase
      .from('services')
      .delete()
      .eq('id', serviceId);

    if (error) throw error;
  },

  // Get service with endpoints
  async getServiceWithEndpoints(serviceId) {
    const { data, error } = await supabase
      .from('services')
      .select(`
        *,
        endpoints(*)
      `)
      .eq('id', serviceId)
      .single();

    if (error) throw error;
    return data;
  },
};

// =====================================================
// ENDPOINTS
// =====================================================

export const endpointHelpers = {
  // Get all endpoints for an organization
  async getAllEndpoints(organizationId) {
    const { data, error } = await supabase
      .from('endpoints')
      .select('*, service:services(*)')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get endpoints for a specific service
  async getEndpointsByService(serviceId) {
    const { data, error } = await supabase
      .from('endpoints')
      .select('*')
      .eq('service_id', serviceId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get endpoint by ID
  async getEndpoint(endpointId) {
    const { data, error } = await supabase
      .from('endpoints')
      .select('*, service:services(*)')
      .eq('id', endpointId)
      .single();

    if (error) throw error;
    return data;
  },

  // Create endpoint
  async createEndpoint(endpoint) {
    const { data, error } = await supabase
      .from('endpoints')
      .insert(endpoint)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update endpoint
  async updateEndpoint(endpointId, updates) {
    const { data, error } = await supabase
      .from('endpoints')
      .update(updates)
      .eq('id', endpointId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete endpoint
  async deleteEndpoint(endpointId) {
    const { error } = await supabase
      .from('endpoints')
      .delete()
      .eq('id', endpointId);

    if (error) throw error;
  },

  // Toggle endpoint active status
  async toggleEndpoint(endpointId, isActive) {
    const { data, error } = await supabase
      .from('endpoints')
      .update({ is_active: isActive })
      .eq('id', endpointId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// =====================================================
// MONITOR LOGS
// =====================================================

export const monitorLogHelpers = {
  // Get recent logs for an endpoint
  async getEndpointLogs(endpointId, limit = 100) {
    const { data, error } = await supabase
      .from('monitor_logs')
      .select('*')
      .eq('endpoint_id', endpointId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  // Get logs for a service
  async getServiceLogs(serviceId, limit = 100) {
    const { data, error } = await supabase
      .from('monitor_logs')
      .select('*, endpoint:endpoints(*)')
      .eq('service_id', serviceId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  // Get logs for an organization
  async getOrganizationLogs(organizationId, limit = 100) {
    const { data, error } = await supabase
      .from('monitor_logs')
      .select('*, endpoint:endpoints(*), service:services(*)')
      .eq('organization_id', organizationId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  // Get uptime statistics for an endpoint
  async getEndpointUptime(endpointId, hoursBack = 24) {
    const since = new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('monitor_logs')
      .select('is_success')
      .eq('endpoint_id', endpointId)
      .gte('timestamp', since);

    if (error) throw error;

    const total = data.length;
    const successful = data.filter((log) => log.is_success).length;
    const uptime = total > 0 ? (successful / total) * 100 : null;

    return {
      uptime: uptime !== null ? uptime.toFixed(2) : null,
      total,
      successful,
      failed: total - successful,
    };
  },

  // Get uptime statistics for a service
  async getServiceUptime(serviceId, hoursBack = 24) {
    const since = new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('monitor_logs')
      .select('is_success')
      .eq('service_id', serviceId)
      .gte('timestamp', since);

    if (error) throw error;

    const total = data.length;
    const successful = data.filter((log) => log.is_success).length;
    const uptime = total > 0 ? (successful / total) * 100 : null;

    return {
      uptime: uptime !== null ? uptime.toFixed(2) : null,
      total,
      successful,
      failed: total - successful,
    };
  },

  // Get average response time for an endpoint
  async getAverageResponseTime(endpointId, hoursBack = 24) {
    const since = new Date(Date.now() - hoursBack * 60 * 60 * 1000).toISOString();

    const { data, error } = await supabase
      .from('monitor_logs')
      .select('response_time_ms')
      .eq('endpoint_id', endpointId)
      .eq('is_success', true)
      .gte('timestamp', since);

    if (error) throw error;

    if (data.length === 0) return 0;

    const sum = data.reduce((acc, log) => acc + (log.response_time_ms || 0), 0);
    return Math.round(sum / data.length);
  },
};

// =====================================================
// INCIDENTS
// =====================================================

export const incidentHelpers = {
  // Get incidents for an endpoint
  async getEndpointIncidents(endpointId) {
    const { data, error } = await supabase
      .from('incidents')
      .select('*')
      .eq('endpoint_id', endpointId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Get active incidents for an organization
  async getActiveIncidents(organizationId) {
    const { data, error } = await supabase
      .from('incidents')
      .select('*, endpoint:endpoints(*), service:services(*)')
      .eq('organization_id', organizationId)
      .eq('is_resolved', false)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  // Create incident
  async createIncident(incident) {
    const { data, error } = await supabase
      .from('incidents')
      .insert(incident)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Resolve incident
  async resolveIncident(incidentId) {
    const { data, error } = await supabase
      .from('incidents')
      .update({
        is_resolved: true,
        resolved_at: new Date().toISOString(),
      })
      .eq('id', incidentId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// =====================================================
// ORGANIZATION MEMBERS
// =====================================================

export const memberHelpers = {
  // Get members of an organization
  async getOrganizationMembers(organizationId) {
    const { data, error } = await supabase
      .from('organization_members')
      .select(`
        *,
        user:users(*)
      `)
      .eq('organization_id', organizationId)
      .order('joined_at', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Add member to organization
  async addMember(organizationId, userId, role, invitedBy) {
    const { data, error } = await supabase
      .from('organization_members')
      .insert({
        organization_id: organizationId,
        user_id: userId,
        role,
        invited_by: invitedBy,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update member role
  async updateMemberRole(organizationId, userId, role) {
    const { data, error } = await supabase
      .from('organization_members')
      .update({ role })
      .eq('organization_id', organizationId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Remove member from organization
  async removeMember(organizationId, userId) {
    const { error } = await supabase
      .from('organization_members')
      .delete()
      .eq('organization_id', organizationId)
      .eq('user_id', userId);

    if (error) throw error;
  },
};

// =====================================================
// AUDIT LOGS
// =====================================================

export const auditLogHelpers = {
  // Create audit log entry
  async createAuditLog(log) {
    const { data, error } = await supabase
      .from('audit_logs')
      .insert({
        ...log,
        timestamp: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get audit logs for an organization
  async getOrganizationAuditLogs(organizationId, limit = 100) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*, user:users(*)')
      .eq('organization_id', organizationId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },

  // Get audit logs for a user
  async getUserAuditLogs(userId, limit = 100) {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*, organization:organizations(*)')
      .eq('user_id', userId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data;
  },
};

// =====================================================
// USERS
// =====================================================

export const userHelpers = {
  // Get or create user profile
  async getOrCreateUserProfile(authUser) {
    try {
      const queryPromise = supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();

      // Timeout to prevent infinite hang on slow initial connections
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Query timeout')), 30000)
      );

      const { data: existingUser, error: selectError } = await Promise.race([
        queryPromise,
        timeoutPromise
      ]);

      if (existingUser) return existingUser;

      // Create profile if it doesn't exist
      if (selectError && selectError.code === 'PGRST116') {
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            id: authUser.id,
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || null,
            avatar_url: authUser.user_metadata?.avatar_url || null,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        return newUser;
      }

      throw selectError;
    } catch (err) {
      throw err;
    }
  },

  // Update user profile
  async updateUserProfile(userId, updates) {
    const { data, error } = await supabase
      .from('users')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// Export all helpers as a single object for convenience
export const supabaseHelpers = {
  organizations: organizationHelpers,
  services: serviceHelpers,
  endpoints: endpointHelpers,
  monitorLogs: monitorLogHelpers,
  incidents: incidentHelpers,
  members: memberHelpers,
  auditLogs: auditLogHelpers,
  users: userHelpers,
};
